import React, { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ReactComponent as AddIcon } from "../../Assets/Icons/AddIcon.svg";
import { PrimaryDialogTitle } from "../../Components/Common/Dialog/DialogComponents";
import * as yup from "yup";
import { useFormik } from "formik";
import { isEmptyObject } from "../../utils/generalUtils";
import { useXssGuard } from "Hooks/useXssGuard";
import "../../Sass/Common.scss";
import PrimaryIcon from "../../Components/Common/PrimaryIcon";
const DrugAutoComplete = React.lazy(() =>
  import("../../Components/Patients/DrugAutoComplete")
);

const useStyle = makeStyles((theme) => ({
  rsPatientAddMed: {
    float: "left",
    "&>button": {
      fontSize: "14px !important",
      fontWeight: "400 !important",
      borderRadius: "5px !important",
    },
  },
}));

const DrugsDialog = (props) => {
  const { open, setOpen, onAddDrug, editMedication } = props;
  const classes = useStyle();
  const initialFormValue = {
    name: "",
    drug_info: {},
    quantity: "",
    changeable_prn: false,
  };
  /** handle close */
  const handleClose = () => {
    setOpen(false);
    resetForm(initialFormValue);
  };

  /** drug schema */
  const drugSchema = yup.object().shape({
    name: yup.string().required("The drug name field is required."),
    quantity: yup.number().integer("Please enter integer number."),
  });

  /** handle add drug */
  const handleAddDrug = (formValues) => {
    let payload = {
      name: formValues.name,
      quantity: formValues.quantity,
      changeable_prn: formValues.changeable_prn,
    };
    onAddDrug(payload, editMedication);
    handleClose();
  };

  const { guardedSubmit: xssGuardedAddDrug } = useXssGuard(handleAddDrug);

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    resetForm,
    setFieldValue,
    handleBlur,
  } = useFormik({
    initialValues: initialFormValue,
    validationSchema: drugSchema,
    enableReinitialize: true,
    onSubmit: xssGuardedAddDrug,
  });

  /** handle set data on open  */
  useEffect(() => {
    if (open) {
      if (!isEmptyObject(editMedication)) {
        setFieldValue("drug_info", editMedication.itemInfo);
        setFieldValue("name", editMedication.itemInfo.name);
        setFieldValue("quantity", editMedication.itemInfo.quantity);
        setFieldValue("changeable_prn", editMedication.itemInfo.changeable_prn);
      }
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        maxWidth={"md"}
        onClose={(e) => {
          if (e.key === "Escape") {
            handleClose();
          }
        }}
        BackdropProps={{
          classes: {
            root: "root",
          },
        }}
      >
        <PrimaryDialogTitle handleClose={handleClose} title="Medications" />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <DialogContent>
            <Grid container columnSpacing={2.5} rowSpacing={3}>
              <Grid item xs={12}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <div style={{ display: "flex" }}>
                          DRUG{" "}
                          <div style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </div>{" "}
                        </div>
                      </TableCell>
                      <TableCell>QUANTITY</TableCell>
                      <TableCell>PRN</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <DrugAutoComplete
                          fullWidth
                          className={classes.autoCompleteBox}
                          value={values?.drug_info}
                          onChange={(e, value) => {
                            value.name = value?.label || "";
                            delete value.label;

                            setFieldValue("drug_info", value);
                            setFieldValue("name", value.name);
                            handleChange();
                          }}
                          onBlur={handleBlur}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Drug Name *"
                              error={touched?.name && errors?.name}
                              helperText={touched?.name && errors?.name}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          fullWidth
                          placeholder="Quantity"
                          label="Quantity"
                          size="small"
                          inputProps={{ maxLength: 10 }}
                          // type="number"
                          name="quantity"
                          value={values?.quantity}
                          error={touched?.quantity && errors?.quantity}
                          helperText={touched?.quantity && errors?.quantity}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </TableCell>
                      <TableCell>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="secondary"
                                checked={values?.changeable_prn}
                                onChange={() =>
                                  setFieldValue(
                                    "changeable_prn",
                                    !values?.changeable_prn
                                  )
                                }
                              />
                            }
                          />
                        </FormGroup>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className={classes.rsPatientAddMed}>
            <Button
              variant="outlined"
              startIcon={
                <PrimaryIcon
                  icon={AddIcon}
                  color="primary"
                  width="16px"
                  height="16px"
                />
              }
              type="submit"
            >
              Add Med
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default DrugsDialog;
